import apiClient from './index';

/**
 * Get all documentation posts
 * @returns {Promise<Array>} - List of documentation posts
 */
export const getAllDocumentationPosts = async () => {
  try {
    const response = await apiClient.get('/adss/v1/documentation');
    return response.data;
  } catch (error) {
    console.error('Error fetching documentation posts:', error);
    throw error;
  }
};

/**
 * Get documentation posts visible to the current user
 * @returns {Promise<Array>} - List of documentation posts
 */
export const getVisibleDocumentationPosts = async () => {
  try {
    const response = await apiClient.get('/adss/v1/documentation/visible');
    return response.data;
  } catch (error) {
    console.error('Error fetching visible documentation posts:', error);
    throw error;
  }
};

/**
 * Get a single documentation post by ID
 * @param {string|number} id - Documentation post ID
 * @returns {Promise<Object>} - Documentation post data
 */
export const getDocumentationPost = async (id) => {
  try {
    const response = await apiClient.get(`/adss/v1/documentation/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching documentation post ${id}:`, error);
    throw error;
  }
};

/**
 * Get a single documentation post by route name
 * @param {string} route - Documentation post route name
 * @returns {Promise<Object>} - Documentation post data
 */
export const getDocumentationPostByRoute = async (route) => {
  try {
    const response = await apiClient.get(`/adss/v1/documentation/${route}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching documentation post by route ${route}:`, error);
    throw error;
  }
};

/**
 * Create a new documentation post
 * @param {Object} postData - Documentation post data
 * @returns {Promise<Object>} - Created documentation post
 */
export const createDocumentationPost = async (postData) => {
  try {
    const response = await apiClient.post('/adss/v1/documentation', postData);
    return response.data;
  } catch (error) {
    console.error('Error creating documentation post:', error);
    throw error;
  }
};

/**
 * Update an existing documentation post
 * @param {string|number} id - Documentation post ID
 * @param {Object} postData - Updated documentation post data
 * @returns {Promise<Object>} - Updated documentation post
 */
export const updateDocumentationPost = async (id, postData) => {
  try {
    const response = await apiClient.put(`/adss/v1/documentation/${id}`, postData);
    return response.data;
  } catch (error) {
    console.error(`Error updating documentation post ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a documentation post
 * @param {string|number} id - Documentation post ID
 * @returns {Promise<void>}
 */
export const deleteDocumentationPost = async (id) => {
  try {
    await apiClient.delete(`/adss/v1/documentation/${id}`);
  } catch (error) {
    console.error(`Error deleting documentation post ${id}:`, error);
    throw error;
  }
};

/**
 * Upload an attachment to a documentation post
 * @param {string|number} postId - Documentation post ID
 * @param {File} file - File to upload
 * @returns {Promise<Object>} - Attachment data
 */
export const uploadDocumentationAttachment = async (postId, file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    // Create a custom config for the form data upload
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };
    
    const response = await apiClient.post(
      `/adss/v1/documentation/${postId}/attachments`,
      formData,
      config
    );
    return response.data;
  } catch (error) {
    console.error(`Error uploading attachment to documentation post ${postId}:`, error);
    throw error;
  }
};

export const uploadMultipleDocumentationAttachments = async (postId, files) => {
  try {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    
    // Create a custom config for the form data upload
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };
    
    const response = await apiClient.post(
      `/adss/v1/documentation/${postId}/attachments/bulk`,
      formData,
      config
    );
    return response.data;
  } catch (error) {
    console.error(`Error uploading multiple attachments to documentation post ${postId}:`, error);
    throw error;
  }
};

/**
 * Delete an attachment from a documentation post
 * @param {string|number} postId - Documentation post ID
 * @param {string|number} attachmentId - Attachment ID
 * @returns {Promise<void>}
 */
export const deleteDocumentationAttachment = async (postId, attachmentId) => {
  try {
    await apiClient.delete(`/adss/v1/documentation/${postId}/attachments/${attachmentId}`);
  } catch (error) {
    console.error(`Error deleting attachment ${attachmentId} from documentation post ${postId}:`, error);
    throw error;
  }
};

/**
 * Get the URL for a documentation attachment
 * @param {string|number} postId - Documentation post ID
 * @param {string} filename - Attachment filename
 * @returns {string} - Attachment URL
 */
export const getDocumentationAttachmentUrl = (postId, filename) => {
  return `/adss/v1/documentation/${postId}/attachments/download/${encodeURIComponent(filename)}`;
};

/**
 * Update the order of documentation posts
 * @param {Array<{id: number, order_index: number}>} orderData - Order data
 * @returns {Promise<void>}
 */
export const updateDocumentationOrder = async (orderData) => {
  try {
    await apiClient.put('/adss/v1/documentation/order', { order: orderData });
  } catch (error) {
    console.error('Error updating documentation order:', error);
    throw error;
  }
};
